import type { SvgIconProps } from '@mui/material/SvgIcon';

import { memo } from 'react';

import SvgIcon from '@mui/material/SvgIcon';

import { BackgroundShape } from './background-shape';

// ----------------------------------------------------------------------

type SvgProps = SvgIconProps & { hideBackground?: boolean };

function UploadIllustration({ hideBackground, sx, ...other }: SvgProps) {
  return (
    <SvgIcon
      viewBox="0 0 240 200"
      xmlns="http://www.w3.org/2000/svg"
      sx={[
        (theme) => ({
          '--primary-main': theme.vars.palette.primary.main,
          '--primary-light': theme.vars.palette.primary.light,
          '--primary-dark': theme.vars.palette.primary.dark,
          '--primary-darker': theme.vars.palette.primary.darker,
          width: 320,
          maxWidth: 1,
          flexShrink: 0,
          height: 'auto',
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      {!hideBackground && <BackgroundShape />}

      <defs>
        <linearGradient id="cloud-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="var(--primary-light)" stopOpacity="0.2" />
          <stop offset="100%" stopColor="var(--primary-main)" stopOpacity="0.1" />
        </linearGradient>
      </defs>

      {/* Decorative circles */}
      <circle cx="30" cy="40" r="3.5" fill="var(--primary-main)" opacity="0.15" />
      <circle cx="210" cy="60" r="2.5" fill="var(--primary-main)" opacity="0.1" />
      <circle cx="200" cy="150" r="4" fill="var(--primary-main)" opacity="0.12" />
      <circle cx="35" cy="160" r="3" fill="var(--primary-main)" opacity="0.08" />

      {/* Cloud background */}
      <g opacity="0.08">
        <path
          fill="var(--primary-main)"
          d="M60 80c0-8.8 7.2-16 16-16 4.4 0 8.4 1.8 11.2 4.8 2-1.2 4.4-2 7-2 8.8 0 16 7.2 16 16 0 0.8-0.2 1.6-0.2 2.4 5.2 1.2 9.2 6 9.2 11.6 0 6.8-5.6 12.4-12.4 12.4H63.2C56.4 129.2 50.8 123.6 50.8 116.8 50.8 111.2 54.8 106.4 60 105.2 60 102 60 100 60 80z"
        />
      </g>

      {/* Main cloud with upload arrow */}
      <g>
        {/* Cloud shape */}
        <path
          fill="url(#cloud-gradient)"
          stroke="var(--primary-main)"
          strokeWidth="1.2"
          d="M55 95c0-10.5 8.5-19 19-19 4.5 0 8.8 1.6 12 4.2 2.2-1.4 4.8-2.2 7.5-2.2 10.5 0 19 8.5 19 19 0 1-0.1 2-0.2 3 7.2 1.2 12.7 7.8 12.7 15.5 0 8.7-7.1 15.8-15.8 15.8H70.8c-8.7 0-15.8-7.1-15.8-15.8 0-7.7 5.5-14.3 12.7-15.5 -0.1-1-0.2-2-0.2-3 0-0.3 0-0.6 0-1"
        />

        {/* Upload arrow - Up chevron */}
        <g transform="translate(120, 95)">
          {/* Arrow shaft */}
          <line x1="0" y1="10" x2="0" y2="-5" stroke="var(--primary-main)" strokeWidth="2" strokeLinecap="round" />

          {/* Arrow head - top point */}
          <path
            fill="none"
            stroke="var(--primary-main)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M -4 -2 L 0 -6 L 4 -2"
          />
        </g>
      </g>

      {/* Document stack - left side */}
      <g>
        {/* Back document - darker */}
        <rect
          x="35"
          y="125"
          width="20"
          height="28"
          rx="1.5"
          fill="var(--primary-main)"
          opacity="0.1"
          transform="rotate(-8 45 139)"
        />

        {/* Middle document */}
        <rect
          x="42"
          y="120"
          width="20"
          height="28"
          rx="1.5"
          fill="var(--primary-light)"
          opacity="0.4"
          transform="rotate(-4 52 134)"
        />

        {/* Front document - white */}
        <rect
          x="49"
          y="118"
          width="20"
          height="28"
          rx="1.5"
          fill="white"
          stroke="var(--primary-main)"
          strokeWidth="0.8"
        />

        {/* Document lines */}
        <line x1="54" y1="125" x2="64" y2="125" stroke="var(--primary-main)" strokeWidth="0.8" opacity="0.3" />
        <line x1="54" y1="130" x2="64" y2="130" stroke="var(--primary-main)" strokeWidth="0.8" opacity="0.3" />
        <line x1="54" y1="135" x2="64" y2="135" stroke="var(--primary-main)" strokeWidth="0.8" opacity="0.3" />
      </g>

      {/* Document stack - right side */}
      <g>
        {/* Back document */}
        <rect
          x="175"
          y="128"
          width="18"
          height="25"
          rx="1.5"
          fill="var(--primary-main)"
          opacity="0.08"
          transform="rotate(10 184 140.5)"
        />

        {/* Front document */}
        <rect
          x="180"
          y="122"
          width="18"
          height="25"
          rx="1.5"
          fill="white"
          stroke="var(--primary-main)"
          strokeWidth="0.8"
          transform="rotate(4 189 134.5)"
        />

        {/* Document lines */}
        <line x1="185" y1="128" x2="192" y2="128" stroke="var(--primary-main)" strokeWidth="0.8" opacity="0.3" transform="rotate(4 189 128)" />
        <line x1="185" y1="133" x2="192" y2="133" stroke="var(--primary-main)" strokeWidth="0.8" opacity="0.3" transform="rotate(4 189 133)" />
      </g>

      {/* Corner accent - top right */}
      <g opacity="0.15">
        <circle cx="210" cy="25" r="0.8" fill="var(--primary-main)" />
        <circle cx="220" cy="20" r="0.6" fill="var(--primary-main)" />
      </g>

      {/* Corner accent - bottom left */}
      <g opacity="0.12">
        <circle cx="25" cy="175" r="0.8" fill="var(--primary-main)" />
        <circle cx="18" cy="182" r="0.6" fill="var(--primary-main)" />
      </g>
    </SvgIcon>
  );
}

export default memo(UploadIllustration);
